import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";

import type { Virtualizer } from "@tanstack/react-virtual";

import {
  pendingScrollToContactIdAtom,
  scrollToLetterFnAtom,
  selectedCategoryAtom,
} from "../stores";
import type { FlatItem } from "../types";

type UseContactListScrollParams = {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  flatItems: FlatItem[];
  virtualizer: Virtualizer<HTMLDivElement, Element>;
};

export function useContactListScroll({
  scrollRef,
  flatItems,
  virtualizer,
}: UseContactListScrollParams) {
  const selectedCategory = useAtomValue(selectedCategoryAtom);
  const setScrollToLetterFn = useSetAtom(scrollToLetterFnAtom);
  const pendingScrollToContactId = useAtomValue(pendingScrollToContactIdAtom);
  const setPendingScrollToContactId = useSetAtom(pendingScrollToContactIdAtom);

  // Reset scroll position when changing category
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedCategory, scrollRef]);

  // Register scroll-to-letter function for the AlphaIndex
  useEffect(() => {
    setScrollToLetterFn(() => (letter: string) => {
      const index = flatItems.findIndex(
        (item) => item.type === "section" && item.letter === letter,
      );

      if (index !== -1) {
        virtualizer.scrollToIndex(index, { align: "start" });
      }
    });

    return () => setScrollToLetterFn(null);
  }, [flatItems, virtualizer, setScrollToLetterFn]);

  // Scroll to a specific contact after creation or selection
  useEffect(() => {
    if (pendingScrollToContactId === null) {
      return;
    }

    const index = flatItems.findIndex(
      (item) =>
        item.type === "item" && item.contact.id === pendingScrollToContactId,
    );

    if (index !== -1) {
      virtualizer.scrollToIndex(index, { align: "center" });
      setPendingScrollToContactId(null);
    }
  }, [
    pendingScrollToContactId,
    flatItems,
    virtualizer,
    setPendingScrollToContactId,
  ]);
}
